import { useState, useEffect } from "react";
import { Users, Mail, Phone, Trash2, Check, Bell, MessageSquare, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "bg-[#22C55E]" },
  { value: "notified", label: "Notified", color: "bg-[#3B82F6]" },
  { value: "fulfilled", label: "Fulfilled", color: "bg-[#D4AF37]" },
  { value: "cancelled", label: "Cancelled", color: "bg-[#71717A]" },
];

const BreederWaitlist = ({ eventId = null }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntry, setExpandedEntry] = useState(null);

  useEffect(() => {
    fetchWaitlist();
  }, [eventId]);

  const fetchWaitlist = async () => {
    try {
      const token = sessionStorage.getItem("auth_token");
      let url = `${API}/api/breeder/waitlist`;
      if (eventId) url += `?event_id=${eventId}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(res.data.entries || []);
    } catch (err) {
      logger.error("Failed to fetch waitlist:", err);
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  };

  const updateEntry = async (entryId, updates) => {
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.patch(`${API}/api/breeder/waitlist/${entryId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Entry updated");
      fetchWaitlist();
    } catch (err) {
      logger.error("Failed to update entry:", err);
      toast.error("Failed to update entry");
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Remove this entry from your waitlist?")) return;
    
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.delete(`${API}/api/breeder/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Entry removed");
      fetchWaitlist();
    } catch (err) {
      logger.error("Failed to delete entry:", err);
      toast.error("Failed to remove entry");
    }
  };

  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  if (loading) {
    return (
      <div className="glass-card p-8">
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-6 bg-[#27272A] rounded w-1/4"></div>
          <div className="h-20 bg-[#27272A] rounded"></div>
          <div className="h-20 bg-[#27272A] rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl text-[#EDEDED] flex items-center gap-3">
            <Users className="w-6 h-6 text-[#D4AF37]" />
            Waitlist Management
          </h2>
          <p className="text-[#71717A] mt-1">
            {entries.length} {entries.length === 1 ? "person" : "people"} on your waitlist
          </p>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Users className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="text-lg text-[#EDEDED] mb-2">No Waitlist Entries</h3>
          <p className="text-[#71717A]">
            When buyers join your waitlist, they'll appear here for you to manage.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry, index) => {
            const statusInfo = getStatusInfo(entry.status);
            const isExpanded = expandedEntry === entry.entry_id;
            
            return (
              <div 
                key={entry.entry_id} 
                className="glass-card overflow-hidden transition-all duration-200"
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-[#18181B]/50 transition-colors"
                  onClick={() => setExpandedEntry(isExpanded ? null : entry.entry_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#27272A] flex items-center justify-center text-[#EDEDED] font-medium">
                        #{entry.position || index + 1}
                      </div>
                      <div>
                        <h4 className="text-[#EDEDED] font-medium">{entry.buyer_name}</h4>
                        <div className="flex items-center gap-3 text-sm text-[#71717A]">
                          <span className="capitalize">{entry.species}</span>
                          {entry.breed && <span>• {entry.breed}</span>}
                          <span>• {new Date(entry.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 ${statusInfo.color} text-black text-xs rounded-full`}>
                        {statusInfo.label}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-[#71717A] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-[#27272A] p-4 bg-[#0A0A0A]/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-[#71717A] mb-1">Contact</p>
                        <div className="space-y-2">
                          <a 
                            href={`mailto:${entry.buyer_email}`}
                            className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#EDEDED]"
                          >
                            <Mail className="w-4 h-4" />
                            {entry.buyer_email}
                          </a>
                          {entry.buyer_phone && (
                            <a 
                              href={`tel:${entry.buyer_phone}`}
                              className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#EDEDED]"
                            >
                              <Phone className="w-4 h-4" />
                              {entry.buyer_phone}
                            </a>
                          )}
                        </div>
                      </div>
                      {entry.preferences && (
                        <div>
                          <p className="text-xs text-[#71717A] mb-1">Preferences</p>
                          <p className="text-sm text-[#A1A1AA]">{entry.preferences}</p>
                        </div>
                      )}
                    </div>

                    {/* Breeder Notes */}
                    <div className="mb-4">
                      <p className="text-xs text-[#71717A] mb-1">Your Notes</p>
                      <textarea
                        defaultValue={entry.breeder_notes || ""}
                        placeholder="Add private notes about this buyer..."
                        className="input-minimal text-sm min-h-[60px]"
                        onBlur={(e) => {
                          if (e.target.value !== (entry.breeder_notes || "")) {
                            updateEntry(entry.entry_id, { breeder_notes: e.target.value });
                          }
                        }}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={entry.status}
                        onChange={(e) => updateEntry(entry.entry_id, { status: e.target.value })}
                        className="input-minimal text-sm py-1.5"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      
                      <button
                        onClick={() => updateEntry(entry.entry_id, { status: "notified" })}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#3F3F46] rounded-lg hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                      >
                        <Bell className="w-4 h-4" />
                        Mark Notified
                      </button>
                      
                      <button
                        onClick={() => updateEntry(entry.entry_id, { status: "fulfilled" })}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#3F3F46] rounded-lg hover:border-[#22C55E] hover:text-[#22C55E] transition-colors"
                      >
                        <Check className="w-4 h-4" />
                        Mark Fulfilled
                      </button>
                      
                      <a
                        href={`mailto:${entry.buyer_email}?subject=Update from ${entry.species} Waitlist`}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#3F3F46] rounded-lg hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Email
                      </a>
                      
                      <button
                        onClick={() => deleteEntry(entry.entry_id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#3F3F46] rounded-lg hover:border-red-500 hover:text-red-500 transition-colors ml-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BreederWaitlist;
